import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, runTransaction, setDoc, Timestamp } from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { db, storage } from '../../../shared/storage/firebase';
import { useAuth } from '../../auth/contexts/AuthContext';
import type { GlobalDoc, GlobalDocKind, InvoicePdfMeta, InvoiceTemplate } from '../../../types';
import '../styles/AdminBillingPage.css';

function fmtTimestamp(v: any): string {
  if (!v) return '—';
  try {
    if (typeof v?.toDate === 'function') {
      const d = v.toDate();
      if (d instanceof Date && !Number.isNaN(d.getTime())) return d.toLocaleString();
    }
  } catch (_) {}
  return '—';
}

const DEFAULTS: Partial<InvoiceTemplate> = {
  title: 'INVOICE',
  overtureName: 'Overture',
  overtureAddress: 'Chicago, IL',
  paymentInstructions:
    'Payment instructions:\n'
    + '- We prefer ACH. (details provided separately)\n'
    + '- Check: payable to Overture (include invoice # in memo)\n',
  emailReplyTo: 'steven@overtureai.org',
  emailIntroHtml: '',
};

function logoKey(m: any): string {
  const sp = String(m?.storagePath || '').trim();
  if (sp) return sp;
  const url = String(m?.url || '').trim();
  return url || JSON.stringify(m || {});
}

export default function AdminInvoiceTemplatePage() {
  const navigate = useNavigate();
  const { userData } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [template, setTemplate] = useState<InvoiceTemplate>({ id: 'current', ...(DEFAULTS as any) });
  const [globalDocs, setGlobalDocs] = useState<Record<GlobalDocKind, GlobalDoc | null>>({ w9: null, ach: null });

  const templateRef = useMemo(() => doc(db, 'invoiceTemplate', 'current'), []);

  const logoOptions = useMemo((): InvoicePdfMeta[] => {
    const cur: any = template as any;
    const current = cur?.logo ? [cur.logo as InvoicePdfMeta] : [];
    const history = Array.isArray(cur?.logoHistory) ? (cur.logoHistory as InvoicePdfMeta[]) : [];
    const all = [...current, ...history].filter(Boolean);
    const seen = new Set<string>();
    const deduped: InvoicePdfMeta[] = [];
    for (const m of all) {
      const k = logoKey(m);
      if (seen.has(k)) continue;
      seen.add(k);
      deduped.push(m);
    }
    // Newest first (best-effort)
    deduped.sort((a: any, b: any) => Number(b?.version || 0) - Number(a?.version || 0));
    return deduped;
  }, [template]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setInfo(null);
    try {
      const [tSnap, w9Snap, achSnap] = await Promise.all([
        getDoc(templateRef),
        getDoc(doc(db, 'globalDocs', 'w9')),
        getDoc(doc(db, 'globalDocs', 'ach')),
      ]);
      const t = tSnap.exists() ? ({ id: 'current', ...(tSnap.data() as any) } as InvoiceTemplate) : ({ id: 'current', ...(DEFAULTS as any) } as InvoiceTemplate);
      setTemplate({ ...(DEFAULTS as any), ...(t as any), id: 'current' });
      setGlobalDocs({
        w9: w9Snap.exists() ? ({ id: 'w9', ...(w9Snap.data() as any) } as any) : null,
        ach: achSnap.exists() ? ({ id: 'ach', ...(achSnap.data() as any) } as any) : null,
      });
    } catch (e: any) {
      setError(e?.message || 'Failed to load invoice template.');
    } finally {
      setLoading(false);
    }
  }, [templateRef]);

  useEffect(() => {
    load();
  }, [load]);

  const saveTemplate = useCallback(async () => {
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      const { id, ...rest } = template as any;
      await setDoc(templateRef, {
        ...rest,
        updatedAt: Timestamp.now(),
        updatedBy: userData?.id || '',
      } as any, { merge: true });
      setInfo('Template saved.');
    } catch (e: any) {
      setError(e?.message || 'Failed to save template.');
    } finally {
      setSaving(false);
    }
  }, [template, templateRef, userData?.id]);

  const uploadGlobalDoc = useCallback(async (kind: GlobalDocKind, file: File) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setError('Please upload a PDF.');
      return;
    }
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      const dref = doc(db, 'globalDocs', kind);
      const meta = await runTransaction(db, async (tx) => {
        const snap = await tx.get(dref);
        const cur: any = snap.exists() ? (snap.data() as any) : {};
        const current = cur?.current || null;
        const nextVersion = Math.max(0, Number(current?.version || 0)) + 1;
        const path = `globalDocs/${kind}/v${nextVersion}.pdf`;
        return { path, nextVersion, prev: current as InvoicePdfMeta | null };
      });

      const r = storageRef(storage, meta.path);
      await uploadBytes(r, file, { contentType: 'application/pdf' });
      const url = await getDownloadURL(r);
      const newMeta: InvoicePdfMeta = {
        storagePath: meta.path,
        url,
        filename: file.name || `${kind}.pdf`,
        version: meta.nextVersion,
        uploadedAt: Timestamp.now(),
        uploadedBy: userData?.id || '',
      };

      await runTransaction(db, async (tx) => {
        const snap = await tx.get(dref);
        const cur: any = snap.exists() ? (snap.data() as any) : {};
        const history: InvoicePdfMeta[] = Array.isArray(cur?.history) ? cur.history : [];
        const prev = cur?.current ? (cur.current as InvoicePdfMeta) : null;
        const nextHistory = prev ? [...history, prev] : history;
        tx.set(dref, {
          kind,
          current: newMeta,
          history: nextHistory,
          updatedAt: Timestamp.now(),
          updatedBy: userData?.id || '',
        } as any, { merge: true });
      });

      await load();
      setInfo(`${kind.toUpperCase()} uploaded.`);
    } catch (e: any) {
      setError(e?.message || `Failed to upload ${kind}.`);
    } finally {
      setSaving(false);
    }
  }, [load, userData?.id]);

  const uploadLogo = useCallback(async (file: File) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image (png/jpg/webp).');
      return;
    }
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      const meta = await runTransaction(db, async (tx) => {
        const snap = await tx.get(templateRef);
        const cur: any = snap.exists() ? (snap.data() as any) : {};
        const current = cur?.logo || null;
        const nextVersion = Math.max(0, Number(current?.version || 0)) + 1;
        const ext = (file.name.split('.').pop() || 'png').toLowerCase();
        const path = `invoiceTemplate/logo/v${nextVersion}.${ext}`;
        return { path, nextVersion };
      });

      const r = storageRef(storage, meta.path);
      await uploadBytes(r, file, { contentType: file.type || 'image/png' });
      const url = await getDownloadURL(r);
      const logo: InvoicePdfMeta = {
        storagePath: meta.path,
        url,
        filename: file.name || 'logo',
        version: meta.nextVersion,
        uploadedAt: Timestamp.now(),
        uploadedBy: userData?.id || '',
      };
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(templateRef);
        const cur: any = snap.exists() ? (snap.data() as any) : {};
        const prev = cur?.logo || null;
        const history: InvoicePdfMeta[] = Array.isArray(cur?.logoHistory) ? cur.logoHistory : [];
        const nextHistoryRaw = prev ? [prev, ...history] : history;
        const seen = new Set<string>();
        const nextHistory: InvoicePdfMeta[] = [];
        for (const m of nextHistoryRaw) {
          const k = logoKey(m);
          if (seen.has(k)) continue;
          seen.add(k);
          // Never store the newly uploaded logo in history.
          if (logoKey(logo) === k) continue;
          nextHistory.push(m);
        }
        tx.set(templateRef, {
          logo,
          logoHistory: nextHistory,
          updatedAt: Timestamp.now(),
          updatedBy: userData?.id || '',
        } as any, { merge: true });
      });
      await load();
      setInfo('Logo uploaded.');
    } catch (e: any) {
      setError(e?.message || 'Failed to upload logo.');
    } finally {
      setSaving(false);
    }
  }, [load, templateRef, userData?.id]);

  const selectLogo = useCallback(async (meta: InvoicePdfMeta) => {
    const targetKey = logoKey(meta);
    if (!targetKey) return;
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(templateRef);
        const cur: any = snap.exists() ? (snap.data() as any) : {};
        const current = cur?.logo || null;
        const history: InvoicePdfMeta[] = Array.isArray(cur?.logoHistory) ? cur.logoHistory : [];

        if (current && logoKey(current) === targetKey) return;

        // Remove selected from history (if present)
        const remaining = history.filter(m => logoKey(m) !== targetKey);

        // If we had a current, push it into history.
        const nextHistoryRaw = current ? [current, ...remaining] : remaining;
        const seen = new Set<string>();
        const nextHistory: InvoicePdfMeta[] = [];
        for (const m of nextHistoryRaw) {
          const k = logoKey(m);
          if (!k) continue;
          if (seen.has(k)) continue;
          seen.add(k);
          nextHistory.push(m);
        }

        tx.set(templateRef, {
          logo: meta,
          logoHistory: nextHistory,
          updatedAt: Timestamp.now(),
          updatedBy: userData?.id || '',
        } as any, { merge: true });
      });
      await load();
      setInfo('Logo updated.');
    } catch (e: any) {
      setError(e?.message || 'Failed to select logo.');
    } finally {
      setSaving(false);
    }
  }, [load, templateRef, userData?.id]);

  const deleteLogo = useCallback(async (meta: InvoicePdfMeta) => {
    const sp = String((meta as any)?.storagePath || '').trim();
    if (!sp) {
      setError('Cannot delete: missing storage path.');
      return;
    }
    const ok = await window.appConfirm('Delete this logo? This will remove it from Storage and the template history.');
    if (!ok) return;
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      await deleteObject(storageRef(storage, sp));

      await runTransaction(db, async (tx) => {
        const snap = await tx.get(templateRef);
        const cur: any = snap.exists() ? (snap.data() as any) : {};
        const current = cur?.logo || null;
        const history: InvoicePdfMeta[] = Array.isArray(cur?.logoHistory) ? cur.logoHistory : [];
        const targetKey = logoKey(meta);

        const nextHistory = history.filter(m => logoKey(m) !== targetKey);
        const currentIsDeleted = current && logoKey(current) === targetKey;

        let nextLogo: InvoicePdfMeta | null = currentIsDeleted ? null : current;
        let finalHistory = nextHistory;

        if (currentIsDeleted) {
          // Promote the newest remaining logo, if any.
          const sorted = nextHistory.slice().sort((a: any, b: any) => Number(b?.version || 0) - Number(a?.version || 0));
          nextLogo = sorted[0] || null;
          if (nextLogo) finalHistory = nextHistory.filter(m => logoKey(m) !== logoKey(nextLogo));
        }

        tx.set(templateRef, {
          logo: nextLogo,
          logoHistory: finalHistory,
          updatedAt: Timestamp.now(),
          updatedBy: userData?.id || '',
        } as any, { merge: true });
      });

      await load();
      setInfo('Logo deleted.');
    } catch (e: any) {
      setError(e?.message || 'Failed to delete logo.');
    } finally {
      setSaving(false);
    }
  }, [load, templateRef, userData?.id]);

  return (
    <div className="billing-page">
      <div className="billing-card">
        <div className="billing-header">
          <button
            className="billing-back"
            onClick={() => navigate('/app/admin', { state: { adminTab: 'invoicing' } })}
          >
            ← Back
          </button>
          <div style={{ flex: 1 }}>
            <div className="billing-title">Invoice Template</div>
            <div className="billing-subtitle">Global invoice configuration (admin)</div>
          </div>
        </div>

        {error && <div className="billing-error">{error}</div>}
        {info && <div className="billing-info">{info}</div>}

        <div className="billing-section" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
          <div className="logo-picker">
            <div className="logo-picker-main">
              <div className="logo-picker-preview">
                {(template as any)?.logo?.url ? (
                  <img
                    className="logo-picker-preview-img"
                    src={(template as any).logo.url}
                    alt="Current invoice logo"
                  />
                ) : (
                  <div className="muted">No logo uploaded yet.</div>
                )}
              </div>

              <div className="logo-picker-upload">
                <input
                  type="file"
                  accept="image/*"
                  disabled={saving || loading}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadLogo(f);
                    e.currentTarget.value = '';
                  }}
                />
              </div>
            </div>

            {logoOptions.length > 0 && (
              <div className="logo-picker-grid" aria-label="Logo gallery">
                {logoOptions.map((m) => {
                  const k = logoKey(m);
                  const isCurrent = Boolean((template as any)?.logo && logoKey((template as any).logo) === k);
                  return (
                    <div key={k} className={`logo-thumb ${isCurrent ? 'is-current' : ''}`}>
                      <button
                        type="button"
                        className="logo-thumb-btn"
                        onClick={() => selectLogo(m)}
                        disabled={saving || loading || isCurrent}
                        title={isCurrent ? 'Current logo' : 'Set as current logo'}
                      >
                        <img className="logo-thumb-img" src={(m as any).url} alt={(m as any).filename || 'Logo'} />
                      </button>
                      <button
                        type="button"
                        className="logo-thumb-del"
                        onClick={() => deleteLogo(m)}
                        disabled={saving || loading}
                        aria-label="Delete logo"
                        title="Delete logo"
                      >
                        ✕
                      </button>
                      {isCurrent && <div className="logo-thumb-badge">Current</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="billing-section">
          <div className="grid-2">
            <div className="field">
              <label>Invoice title</label>
              <input value={template.title || ''} disabled={saving || loading} onChange={(e) => setTemplate(t => ({ ...t, title: e.target.value }))} />
            </div>
            <div className="field">
              <label>Reply-to email (invoice emails)</label>
              <input value={template.emailReplyTo || ''} disabled={saving || loading} onChange={(e) => setTemplate(t => ({ ...t, emailReplyTo: e.target.value }))} />
            </div>
            <div className="field">
              <label>Overture business name</label>
              <input value={template.overtureName || ''} disabled={saving || loading} onChange={(e) => setTemplate(t => ({ ...t, overtureName: e.target.value }))} />
            </div>
            <div className="field">
              <label>Overture business address</label>
              <input value={template.overtureAddress || ''} disabled={saving || loading} onChange={(e) => setTemplate(t => ({ ...t, overtureAddress: e.target.value }))} />
            </div>
          </div>
          <div className="field" style={{ marginTop: 10 }}>
            <label>Payment instructions (used on invoices + in copied data)</label>
            <textarea rows={6} value={template.paymentInstructions || ''} disabled={saving || loading} onChange={(e) => setTemplate(t => ({ ...t, paymentInstructions: e.target.value }))} />
          </div>
          <div className="field" style={{ marginTop: 10 }}>
            <label>Email intro HTML (optional)</label>
            <textarea rows={6} value={template.emailIntroHtml || ''} disabled={saving || loading} onChange={(e) => setTemplate(t => ({ ...t, emailIntroHtml: e.target.value }))} />
            <div className="muted">Used in the invoice email body (MVP). Keep it short.</div>
          </div>
          <div className="actions-row">
            <button onClick={saveTemplate} disabled={saving || loading}>Save template</button>
          </div>
        </div>

        <div className="billing-section">
          <div className="section-title">Global attachments (admin)</div>
          <div className="muted">These PDFs will be attached to invoice emails (future sends only).</div>
          <div className="grid-2" style={{ marginTop: 10 }}>
            <div className="global-doc-card">
              <div className="global-doc-top">
                <div className="global-doc-title">W‑9 PDF</div>
                {globalDocs.w9?.current?.storagePath ? <span className="global-doc-badge ok">Current</span> : <span className="global-doc-badge warn">Missing</span>}
              </div>
              {globalDocs.w9?.current?.storagePath ? (
                <>
                  <div className="global-doc-meta">
                    <div><b>Version:</b> v{globalDocs.w9.current.version || 1}</div>
                    <div><b>File:</b> {globalDocs.w9.current.filename || 'W-9.pdf'}</div>
                    <div><b>Uploaded:</b> {fmtTimestamp(globalDocs.w9.current.uploadedAt)}</div>
                  </div>
                  <div className="global-doc-actions">
                    <a className="global-doc-link" href={globalDocs.w9.current.url} target="_blank" rel="noreferrer">Download current</a>
                  </div>
                </>
              ) : <div className="muted">No W‑9 uploaded yet.</div>}
              <div className="global-doc-upload">
                <label className="global-doc-upload-label">Upload new version</label>
                <input type="file" accept="application/pdf" disabled={saving || loading} onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadGlobalDoc('w9', f);
                  e.currentTarget.value = '';
                }} />
              </div>
            </div>

            <div className="global-doc-card">
              <div className="global-doc-top">
                <div className="global-doc-title">ACH PDF</div>
                {globalDocs.ach?.current?.storagePath ? <span className="global-doc-badge ok">Current</span> : <span className="global-doc-badge warn">Missing</span>}
              </div>
              {globalDocs.ach?.current?.storagePath ? (
                <>
                  <div className="global-doc-meta">
                    <div><b>Version:</b> v{globalDocs.ach.current.version || 1}</div>
                    <div><b>File:</b> {globalDocs.ach.current.filename || 'ACH.pdf'}</div>
                    <div><b>Uploaded:</b> {fmtTimestamp(globalDocs.ach.current.uploadedAt)}</div>
                  </div>
                  <div className="global-doc-actions">
                    <a className="global-doc-link" href={globalDocs.ach.current.url} target="_blank" rel="noreferrer">Download current</a>
                  </div>
                </>
              ) : <div className="muted">No ACH uploaded yet.</div>}
              <div className="global-doc-upload">
                <label className="global-doc-upload-label">Upload new version</label>
                <input type="file" accept="application/pdf" disabled={saving || loading} onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadGlobalDoc('ach', f);
                  e.currentTarget.value = '';
                }} />
              </div>
            </div>
          </div>
        </div>

        {loading ? <div className="muted">Loading…</div> : null}
      </div>
    </div>
  );
}
